import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { API } from '../config/api';
import {
  Briefcase, MapPin, DollarSign, Clock, User, ArrowLeft,
  Loader2, Eye, MessageSquare, Share2, Trash2, Edit,
  Calendar
} from 'lucide-react';

const ROLE_COLORS = {
  artist: 'bg-violet-500/20 text-violet-400 border-violet-500/30',
  producer: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  engineer: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  studio: 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30',
  videographer: 'bg-sky-500/20 text-sky-400 border-sky-500/30',
  graphic_designer: 'bg-pink-500/20 text-pink-400 border-pink-500/30',
  podcaster: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  streamer: 'bg-red-500/20 text-red-400 border-red-500/30',
};

function formatBudget(gig) {
  if (!gig.budget_min && !gig.budget_max) return null;
  if (gig.budget_min && gig.budget_max) return `$${gig.budget_min} - $${gig.budget_max}`;
  if (gig.budget_max) return `Up to $${gig.budget_max}`;
  if (gig.budget_min) return `From $${gig.budget_min}`;
  return null;
}

export default function GigDetailPage() {
  const { gigId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, token } = useAuth();
  const [gig, setGig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [responding, setResponding] = useState(false);

  useEffect(() => {
    fetchGig();
  }, [gigId]);

  const fetchGig = async () => {
    try {
      const res = await fetch(`${API}/gigs/${gigId}`);
      if (res.ok) {
        const d = await res.json();
        setGig(d.gig);
      } else {
        toast.error('Gig not found');
        navigate('/hire-board');
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleRespond = async () => {
    if (!isAuthenticated) { navigate('/login'); return; }
    setResponding(true);
    try {
      // 1. Record response on the gig
      const respRes = await fetch(`${API}/gigs/${gigId}/respond`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (!respRes.ok) {
        const err = await respRes.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to respond');
      }
      const respData = await respRes.json();

      // 2. Start a message conversation with auto-filled text
      const convRes = await fetch(`${API}/messages/conversations`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          recipient_user_id: respData.poster_id || gig.user_id,
          initial_message: `Hi! I'm interested in your gig: "${gig.title}". I'd love to discuss the details.`,
        }),
      });
      if (convRes.ok) {
        const convData = await convRes.json();
        toast.success('Response sent! Opening conversation...');
        navigate(`/messages/${convData.conversation_id}`);
      } else {
        toast.success('Interest recorded! Send them a message to follow up.');
      }
    } catch (err) {
      toast.error(err.message);
    }
    setResponding(false);
  };

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/hire-board/${gigId}`);
      toast.success('Gig link copied!');
    } catch { toast.error('Could not copy link'); }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this gig?')) return;
    try {
      const res = await fetch(`${API}/gigs/${gigId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        toast.success('Gig deleted');
        navigate('/hire-board');
      }
    } catch (e) { toast.error('Failed to delete'); }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin" /></div>;
  }

  if (!gig) {
    return <div className="min-h-screen flex items-center justify-center"><p className="text-muted-foreground">Gig not found</p></div>;
  }

  const isOwner = user?.user_id === gig.user_id;
  const budget = formatBudget(gig);
  const loc = gig.location_neighborhood || gig.location_city;

  return (
    <div className="min-h-screen py-8 px-4" data-testid="gig-detail-page">
      <div className="max-w-3xl mx-auto">
        {/* Back */}
        <Button variant="ghost" className="mb-4" onClick={() => navigate('/hire-board')} data-testid="back-to-board">
          <ArrowLeft className="w-4 h-4 mr-2" />Hire Board
        </Button>

        <Card className="glass-card border-border p-6 md:p-8">
          {/* Status */}
          <div className="flex items-center justify-between mb-4">
            <Badge className={gig.status === 'open' ? 'bg-green-500/20 text-green-400' : 'bg-muted/40 text-muted-foreground'}>
              {gig.status}
            </Badge>
            <span className="text-xs text-muted-foreground">{new Date(gig.created_at).toLocaleDateString()}</span>
          </div>

          {/* Title */}
          <h1 className="font-heading text-2xl md:text-3xl font-bold mb-3">{gig.title}</h1>

          {/* Roles */}
          <div className="flex items-center gap-2 flex-wrap mb-4">
            <span className="text-sm text-muted-foreground mr-1">Looking for:</span>
            {gig.roles_needed?.map(r => (
              <Badge key={r} className={`text-xs ${ROLE_COLORS[r] || 'bg-muted/40 text-muted-foreground'}`}>
                {r.replace('_', ' ')}
              </Badge>
            ))}
          </div>

          {/* Meta */}
          <div className="flex items-center gap-5 text-sm text-muted-foreground flex-wrap mb-6">
            <span
              className="flex items-center gap-1 cursor-pointer hover:text-foreground transition-colors"
              onClick={() => navigate(`/u/${gig.poster_handle}`)}
            >
              <User className="w-4 h-4" />@{gig.poster_handle}
            </span>
            {loc && <span className="flex items-center gap-1"><MapPin className="w-4 h-4" />{loc}</span>}
            {budget && <span className="flex items-center gap-1"><DollarSign className="w-4 h-4" />{budget}</span>}
            {gig.timeline && <span className="flex items-center gap-1"><Clock className="w-4 h-4" />{gig.timeline}</span>}
          </div>

          {/* Description */}
          {gig.description && (
            <div className="mb-6">
              <h3 className="font-medium text-sm text-muted-foreground mb-2">Details</h3>
              <p className="text-sm leading-relaxed whitespace-pre-wrap">{gig.description}</p>
            </div>
          )}

          {/* Stats */}
          <div className="flex items-center gap-6 text-sm text-muted-foreground mb-6 pt-4 border-t border-border">
            <span className="flex items-center gap-1"><Eye className="w-4 h-4" />{gig.views_count || 0} views</span>
            <span className="flex items-center gap-1"><MessageSquare className="w-4 h-4" />{gig.responses_count || 0} responses</span>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3 flex-wrap">
            {!isOwner && gig.status === 'open' && (
              <Button
                onClick={handleRespond}
                disabled={responding}
                className="bg-secondary hover:bg-secondary/80"
                data-testid="respond-gig-btn"
              >
                {responding ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <MessageSquare className="w-4 h-4 mr-2" />}
                {responding ? 'Sending...' : "I'm Interested"}
              </Button>
            )}
            <Button variant="outline" onClick={handleShare} data-testid="share-gig-btn">
              <Share2 className="w-4 h-4 mr-2" />Share
            </Button>
            {isOwner && (
              <Button variant="ghost" className="text-red-500" onClick={handleDelete} data-testid="delete-gig-btn">
                <Trash2 className="w-4 h-4 mr-2" />Delete
              </Button>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
